import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CampaignInterface, CampaignTypeStatus } from '../../../../interfaces/campaigns/campaign.interface';
import { GroupInterface } from '../../../../interfaces/groups/group.interface';
import { MarketingCampaignsService } from '../../../../services/marketing-campaigns/marketing-campaigns.service';
import {
  atLeastOneChecked
} from '../marketing-campaign-create/marketing-campaign-create.component';

export type MarketingCampaignEditResultSave = {
  updated: true,
  editedCampaign: any
};
export type MarketingCampaignEditResultCancel = {
  updated: false
};

export type MarketingCampaignEditResult = MarketingCampaignEditResultCancel | MarketingCampaignEditResultSave;

export type MarketingCampaignEditDependencies = {
  campaign: CampaignInterface,
  groups: GroupInterface[]
};

@Component({
  selector: 'app-marketing-campaign-edit',
  templateUrl: './marketing-campaign-edit.component.html',
  styleUrls: ['./marketing-campaign-edit.component.scss']
})
export class MarketingCampaignEditComponent {

  protected form: FormGroup | undefined;
  protected groups: GroupInterface[] | undefined;

  private readonly campaignId: string;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<MarketingCampaignEditResult>,
    private marketingCampaignsService: MarketingCampaignsService,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: MarketingCampaignEditDependencies) {
    this.campaignId = data.campaign.id;
    this.groups = data.groups;
    this.form = new FormGroup({
      name: new FormControl(this.data.campaign.name, Validators.required),
      description: new FormControl(this.data.campaign.description, Validators.required),
      channel: new FormControl('email'),
      groups: this.fb.array(data.groups.map(
        g => new FormControl(data.campaign.groups.some(gP => gP.id === g.id))), atLeastOneChecked()
      ),
      data: new FormGroup({
        body: new FormControl(this.data.campaign.data.body),
        title: new FormControl(this.data.campaign.data.title),
        footer: new FormControl(this.data.campaign.data.footer),
        header: new FormControl(this.data.campaign.data.header),
        subject: new FormControl(this.data.campaign.data.subject)
      })
    });
    this.form.get('name')?.disable();
    if (this.data.campaign.status !== CampaignTypeStatus.Created) {
      this.form.disable();
    }
    this.dialogRef.backdropClick().subscribe(() => {
      this.dialogRef.close({ updated: false });
    });
  }

  close(): void {
    this.dialogRef.close({ updated: false });
  }

  async update(): Promise<void> {
    const rawData = this.form?.getRawValue();
    const selectedGroupIds = this.groups?.filter((_, i) => rawData.groups[i]).map(g => g.id) || [];
    const editedCampaign = { ...rawData, groupIds: selectedGroupIds };
    try {
      await this.marketingCampaignsService.updateById(this.campaignId, editedCampaign);
      this.dialogRef.close({ updated: true, newCampaign: rawData });
    } catch (error) {
      this.snackBar.open('Error while updating a Campaign. Try again in a few minutes.');
    }
  }
}
